import { useRef, useState } from 'react';

import { formMeta } from '../data/forms.js';
import type { Form } from '../fixtures/domain.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { useDismiss } from '../util/useDismiss.js';
import { FormPreview } from './FormPreview.js';
import styles from './FormPickerDialog.module.css';

/**
 * Pick a form and read it in the same breath, then send it. Selecting a row
 * swaps the preview beside it, so comparing two costs one click. The note is
 * the picker's own, never the composer's draft — sending a form is its own
 * act, and it must not swallow whatever is half-typed below.
 */
export function FormPickerDialog({
  forms,
  sending,
  error = false,
  onClose,
  onSend,
  onManage,
  contained = false,
}: {
  forms: Form[];
  sending: boolean;
  /** The library read failed, so an empty list means nothing yet. */
  error?: boolean;
  onClose: () => void;
  onSend: (form: Form, note: string) => void;
  /** Out to the Forms section, the only place one is written. Omitted where there
   *  is no Forms section to reach — e.g. the widget — so the link is hidden. */
  onManage?: () => void;
  /** Scope the modal to a floating panel (the widget) instead of the viewport. */
  contained?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, dialogRef);
  const [selectedId, setSelectedId] = useState(forms[0]?.id ?? null);
  const [note, setNote] = useState('');

  const selected = forms.find((form) => form.id === selectedId) ?? null;

  return (
    <div className={contained ? styles.overlayContained : styles.overlay}>
      <div
        // Fixed rather than capped once there are rows: sized to its contents
        // the dialog grew and shrank as the selection moved, and the footer
        // buttons jumped under a cursor still choosing. The preview is the
        // part that varies, so the preview scrolls and the dialog holds still.
        className={`${forms.length === 0 ? styles.dialog : styles.dialogFixed}${contained ? ` ${styles.contained}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Send a form"
        ref={dialogRef}
      >
        <div className={styles.header}>
          <span className={styles.iconBadge}>
            <Icon name="listChecks" size={18} />
          </span>
          <div className={styles.heading}>
            <h2 className={styles.title}>Send a form</h2>
            <p className={styles.subtitle}>Sent into this chat as a card.</p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        {error ? (
          <div className={styles.empty}>
            <EmptyState
              icon={<Icon name="listChecks" size={32} />}
              title="Couldn't load your forms"
              description="Close this and try again in a moment."
            />
          </div>
        ) : forms.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState
              icon={<Icon name="listChecks" size={32} />}
              title="No forms yet"
              description="Write one in Forms, then send it here."
            />
            {onManage && <Button onClick={onManage}>Open Forms</Button>}
          </div>
        ) : (
          <>
            <div className={styles.body}>
              <div className={styles.list}>
                {forms.map((form) => (
                  <button
                    key={form.id}
                    type="button"
                    className={form.id === selectedId ? styles.rowActive : styles.row}
                    aria-current={form.id === selectedId}
                    onClick={() => setSelectedId(form.id)}
                  >
                    <span className={styles.rowName}>{form.name}</span>
                    <span className={styles.rowMeta}>{formMeta(form)}</span>
                  </button>
                ))}
              </div>

              <div className={styles.preview}>
                {selected && <FormPreview fields={selected.fields} />}
              </div>
            </div>

            <div className={styles.noteRow}>
              <label className={styles.noteLabel} htmlFor="form-note">
                Message
                <span className={styles.optional}>Optional</span>
              </label>
              <input
                id="form-note"
                className={styles.note}
                value={note}
                placeholder="A few quick questions before we go further."
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </>
        )}

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {forms.length > 0 && (
            <Button
              disabled={selected === null || sending}
              onClick={() => selected && onSend(selected, note)}
            >
              Send form
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
